import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Public routes that NEVER need an authenticated session — skip the /me probe
// to avoid harmless 401 console noise (and a needless network round-trip).
const PUBLIC_ONLY_PREFIXES = ['/pay/', '/portal/', '/contractors/', '/accept-invite/', '/review/'];
const PUBLIC_ONLY_EXACT = new Set(['/', '/marketplace', '/developers', '/login', '/signup', '/pricing', '/privacy', '/terms', '/delete-account-request', '/forgot-password', '/reset-password', '/restore-account', '/magic-login']);

const isPublicOnlyPath = (pathname) => {
  if (PUBLIC_ONLY_EXACT.has(pathname)) return true;
  return PUBLIC_ONLY_PREFIXES.some((p) => pathname.startsWith(p));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip /me probe on public-only routes
    if (typeof window !== 'undefined' && isPublicOnlyPath(window.location.pathname)) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Not authenticated or session expired
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const signup = async (name, email, password, referral_code) => {
    const payload = { name, email, password };
    if (referral_code) payload.referral_code = referral_code;
    const response = await axios.post(`${API}/auth/signup`, payload);
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const checkQuota = async () => {
    if (!user) return { can_generate: true, quotes_used: 0, quotes_limit: 3, subscription_status: 'free' };
    
    const response = await axios.post(`${API}/auth/check-quota`);
    return response.data;
  };

  const incrementQuoteUsage = async () => {
    if (!user) return;
    
    await axios.post(`${API}/auth/increment-quote-usage`);
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      login,
      signup,
      logout,
      checkQuota,
      incrementQuoteUsage,
      refreshUser: checkAuth,
      isAuthenticated: !!user,
      isPro: user?.subscription_status === 'pro'
    }}>
      {children}
    </AuthContext.Provider>
  );
};